'use client';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  InputLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { formatter, usePrevious } from '@repo/utils';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import _ from 'lodash';
import { useRouter, useSearchParams } from 'next/navigation';
import { KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { filterField } from '..';
import { COLORS } from '../../../constants';
import { Dropdown } from '../../dropdown';
import { MaterialIcon } from '../../materialIcon';

/**
 * Filters drawer component
 * - Renders a right-side drawer with configurable filter fields.
 * - Supports client-side filtering (updates URL search params) and server-side filtering (pushes to router to trigger server fetch).
 * @param fields - Array of filterField objects defining each filter input.
 * @param isServerSideFilter - If true, operates in server-side mode (default: false).
 * @param serverFilterTrigger - External trigger to indicate server-side filtering is complete.
 * @param open - Whether the drawer is open.
 * @param onClose - Function to call when closing the drawer.
 */

export const Filters = ({
  fields,
  isServerSideFilter = false,
  serverFilterTrigger,
  open,
  onClose,
}: {
  fields: filterField[];
  isServerSideFilter?: boolean;
  serverFilterTrigger?: number;
  open: boolean;
  onClose: () => void;
}) => {
  const searchParams = useSearchParams();
  const router = useRouter();

  // Initialize values from current URL search params
  const initialValues = useMemo(() => {
    return Array.from(searchParams.entries()).reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
  }, []);

  const [values, setValues] = useState<{ [key: string]: string }>(initialValues);
  const [loading, setLoading] = useState(false);
  const prevTrigger = usePrevious(serverFilterTrigger);

  // Remove empty/null/undefined values from an object
  const cleanValues = useCallback((currentValues: { [key: string]: any }) => {
    const cleaned: { [key: string]: any } = {};
    Object.entries(currentValues).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        cleaned[key] = value;
      }
    });
    return cleaned;
  }, []);

  // Build a querystring from current values, optionally removing pagination params when filtersOnly = true
  const getCurrentString = useCallback(
    (filtersOnly?: boolean) => {
      const current = new URLSearchParams(isServerSideFilter ? window.location.search : {});
      fields.forEach(({ name, dateNames }) => {
        const dateFromName = dateNames?.[0] as string;
        const dateToName = dateNames?.[1] as string;
        if (values[name]) {
          current.set(name, values[name]);
        } else {
          current.delete(name);
        }
        if (values[dateFromName]) {
          current.set(dateFromName, values[dateFromName]);
        } else {
          current.delete(dateFromName);
        }
        if (values[dateToName]) {
          current.set(dateToName, values[dateToName]);
        } else {
          current.delete(dateToName);
        }
      });
      if (filtersOnly) {
        current.delete('page');
        current.delete('offset');
        current.delete('limit');
        return current.toString();
      }
      return current.toString();
    },
    [fields, isServerSideFilter, values, cleanValues],
  );

  // For client-side mode: whenever values change, debounce and update window.history (URL) without navigation
  useEffect(() => {
    if (isServerSideFilter) return;
    const currentFilterValues = getCurrentString(true);
    const searchParamsString = searchParams.toString();

    const sortDirection = searchParams.get('sortDirection');
    const sortIndex = searchParams.get('sortIndex');

    // Current filter values keeping sort data if present
    const sortedFilterValues =
      sortDirection && sortIndex
        ? `${currentFilterValues ? `${currentFilterValues}&` : ''}sortDirection=${sortDirection}&sortIndex=${sortIndex}`
        : currentFilterValues;

    const timeoutRef = setTimeout(() => {
      if (currentFilterValues !== searchParamsString) {
        setLoading(false);
        // Replace URL querystring (no navigation) so other components can read the filters
        window.history.replaceState(null, '', `?${sortedFilterValues}`);
      }
    }, 500);
    return () => {
      clearTimeout(timeoutRef);
    };
  }, [values, fields, isServerSideFilter, getCurrentString, searchParams]);

  // For server-side mode: watch for an external trigger (serverFilterTrigger) to stop loading and close drawer
  useEffect(() => {
    if (!isServerSideFilter || !loading) {
      return;
    }
    if (serverFilterTrigger !== prevTrigger) {
      setLoading(false);
      onClose();
    }
  }, [serverFilterTrigger, prevTrigger, loading, isServerSideFilter, onClose]);

  // When user types/changes a field, set loading for client-side (visual) or not for server-side (handled on submit)
  const handleLoading = useCallback(() => {
    if (!isServerSideFilter) {
      setLoading(true);
    }
  }, [isServerSideFilter]);

  // Performs the filter action:
  // - server-side: push filtered query to router to trigger data fetch
  // - client-side: clear values and set loading to indicate reset (URL is updated by effect)
  const filterAction = useCallback(() => {
    if (isServerSideFilter) {
      const currentString = getCurrentString();
      const filtersString = getCurrentString(true);
      const searchParamsString = searchParams.toString();
      if (currentString !== searchParamsString) {
        setLoading(true);
        router.push(`?${filtersString}`);
      }
    } else {
      setLoading(Object.keys(values).length ? true : false);
      // clear filters (client-side mode treat this as reset)
      setValues({});
    }
  }, [isServerSideFilter, getCurrentString, searchParams, router, values]);

  // Submit filter on Enter key when in server-side mode
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' && isServerSideFilter) {
        event.preventDefault();
        filterAction();
      }
    },
    [isServerSideFilter, filterAction],
  );

  // Clear all filters and navigate to base route (server-side search trigger)
  const handleClear = useCallback(() => {
    setValues({});
    setLoading(true);
    // router.push('?') clears search params and triggers a new search server-side
    router.push('?');
  }, [router]);

  // Handle dependent/dynamic options for selectMultiple fields.
  // If a lead filter is defined, only show options related to selected lead values.
  const handleOptions = useCallback(
    ({
      options,
      dynamicOptions,
      currentFilter,
    }: {
      options: filterField['options'];
      dynamicOptions: filterField['dynamicOptions'];
      currentFilter: string;
    }): { label: string; value: string }[] => {
      const leadFilter = dynamicOptions?.leadFilter;
      const relation = dynamicOptions?.relation;
      if (!dynamicOptions || !leadFilter) {
        return options ?? [];
      }
      const leadFilterValue = values[leadFilter];
      if (!leadFilterValue) {
        if (values[currentFilter]) {
          values[currentFilter] = '';
        }
        return [];
      }
      const parsedLeadFilterValue =
        typeof leadFilterValue === 'string'
          ? leadFilterValue
              .split(',')
              .map((x) => x.trim())
              .filter(Boolean)
          : [];
      const selectedRelations = _.uniq(parsedLeadFilterValue.flatMap((key) => relation?.[key]));
      const filteredOptions = options?.filter((o) => selectedRelations.includes(o.value));
      if (values[currentFilter]) {
        const currentValues =
          typeof values[currentFilter] === 'string'
            ? values[currentFilter]
                .split(',')
                .map((v) => v.trim())
                .filter(Boolean)
            : [];
        const filteredValues = currentValues.filter((v) => selectedRelations.includes(v)).join(',');
        if (values[currentFilter] !== filteredValues) {
          setValues((prev) => ({
            ...prev,
            [currentFilter]: filteredValues,
          }));
        }
      }
      return filteredOptions ?? [];
    },
    [values],
  );

  const datePickerStyles = {
    '& .MuiPickersInputBase-root': {
      backgroundColor: 'white',
      height: '44px',
      borderRadius: '8px',
    },
    '& .MuiPickersOutlinedInput-notchedOutline': {
      borderWidth: '1px !important',
      padding: 0,
      '> legend': {
        width: 0,
        visibility: 'hidden',
      },
    },
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => onClose()}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 400, borderRadius: 0 } },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {/* Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2, pb: 1 }}>
            <Typography variant="h6" color="secondary">
              Filtros
            </Typography>
            <IconButton onClick={() => onClose()}>
              <MaterialIcon icon="close" />
            </IconButton>
          </Stack>
          <Divider />

          {/* Filter fields (generated from fields prop) */}
          <Stack
            spacing={2.5}
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              p: 2.5,
            }}
          >
            {fields.map(
              ({
                name,
                label,
                type = 'text',
                options,
                placeholder,
                startIcon,
                dynamicOptions,
                dateNames,
                length,
                format,
              }) => (
                <Box key={name}>
                  {/* Date picker */}
                  {type === 'date' && (
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
                      <DatePicker
                        slotProps={{ textField: { fullWidth: true, size: 'small', InputLabelProps: { shrink: true } } }}
                        value={values[name] ? dayjs(values[name]) : null}
                        label={label}
                        data-testid={name}
                        name={name}
                        sx={datePickerStyles}
                        onChange={(date) => {
                          handleLoading();
                          const formatted = date ? dayjs(date).format('YYYY-MM-DD') : '';
                          setValues((prev) =>
                            cleanValues({
                              ...prev,
                              [name]: formatted,
                            }),
                          );
                        }}
                      />
                    </LocalizationProvider>
                  )}

                  {/* Date range */}
                  {type === 'dateRange' && dateNames?.length === 2 && (
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
                      {label && <InputLabel>{label}</InputLabel>}
                      <Stack direction="row" gap={1}>
                        {dateNames.map((dateName, index) => (
                          <DatePicker
                            key={dateName}
                            slotProps={{
                              textField: { fullWidth: true, size: 'small', InputLabelProps: { shrink: true } },
                            }}
                            value={values[dateName] ? dayjs(values[dateName]) : null}
                            name={dateName}
                            sx={{
                              ...datePickerStyles,
                              '& .MuiInputAdornment-root': {
                                padding: 0,
                                margin: 0,
                              },
                            }}
                            onChange={(date) => {
                              handleLoading();
                              const formatted = date ? dayjs(date).format('YYYY-MM-DD') : '';
                              setValues((prev) =>
                                cleanValues({
                                  ...prev,
                                  [dateName]: `${formatted}T${index === 0 ? '00:00:00' : '23:59:59'}`,
                                }),
                              );
                            }}
                          />
                        ))}
                      </Stack>
                    </LocalizationProvider>
                  )}

                  {/* Text input */}
                  {type === 'text' && (
                    <TextField
                      fullWidth
                      autoComplete="cc-csc"
                      value={values[name] || ''}
                      label={label}
                      placeholder={placeholder || ''}
                      data-testid={name}
                      name={name}
                      type={type || 'text'}
                      variant="outlined"
                      size="small"
                      onKeyDown={handleKeyDown}
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: 'white',
                        },
                      }}
                      slotProps={{
                        input: {
                          startAdornment: startIcon && (
                            <MaterialIcon icon={startIcon.icon} sx={{ color: startIcon?.color || '' }} />
                          ),
                        },
                      }}
                      onChange={(event) => {
                        handleLoading();
                        setValues((prev) => cleanValues({ ...prev, [name]: event.target.value }));
                      }}
                    />
                  )}

                  {/* Number input (stores only digits) */}
                  {type === 'number' && (
                    <TextField
                      fullWidth
                      value={format ? formatter(values[name], format) : values[name] || ''}
                      label={label}
                      placeholder={placeholder || ''}
                      data-testid={name}
                      name={name}
                      type="text"
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: 'white',
                        },
                      }}
                      slotProps={{
                        htmlInput: { inputMode: 'numeric', maxLength: length || undefined },
                        input: {
                          startAdornment: startIcon && (
                            <MaterialIcon icon={startIcon.icon} sx={{ color: startIcon?.color || '' }} />
                          ),
                        },
                      }}
                      onChange={(event) => {
                        handleLoading();
                        const numberValue = (event.target as HTMLInputElement)?.value.replaceAll(/\D/g, '');
                        setValues((prev) => cleanValues({ ...prev, [name]: numberValue }));
                      }}
                    />
                  )}

                  {/* Single select */}
                  {type === 'select' && options && (
                    <Dropdown
                      fullWidth
                      value={values[name] || ''}
                      label={label}
                      placeholder={placeholder || ''}
                      name={name}
                      data-testid={name}
                      options={options}
                      sx={{ backgroundColor: 'white' }}
                      onChange={(event) => {
                        handleLoading();
                        setValues((prev) => cleanValues({ ...prev, [name]: event.target.value as string }));
                      }}
                    />
                  )}

                  {/* Multiple select with optional dynamic options based on another field */}
                  {type === 'selectMultiple' && options && (
                    <Dropdown
                      fullWidth
                      multiple
                      value={(values[name]?.length || 0) > 0 ? values[name]?.split(',') : []}
                      label={label}
                      name={name}
                      disabled={dynamicOptions?.leadFilter ? !values[dynamicOptions.leadFilter] : false}
                      sx={{ backgroundColor: 'white' }}
                      data-testid={name}
                      renderValue={(selected) => {
                        const selectedValues = Array.isArray(selected) ? selected : [];
                        return selectedValues.length === 0 ? (
                          <span style={{ color: COLORS.text.placeholder, fontSize: '1rem' }}>
                            Seleccioná una opción
                          </span>
                        ) : (
                          <span style={{ fontSize: '1rem' }}>
                            {selectedValues
                              .map((val) => options.find((opt) => opt.value === val)?.label || val)
                              .join(', ')}
                          </span>
                        );
                      }}
                      options={handleOptions({ options, dynamicOptions, currentFilter: name })}
                      onChange={(event) => {
                        const {
                          target: { value },
                        } = event;
                        handleLoading();
                        setValues((prev) => cleanValues({ ...prev, [name]: (value as string[]).join(',') }));
                      }}
                    />
                  )}
                </Box>
              ),
            )}
          </Stack>

          {/* Footer actions */}
          <Box sx={{ p: 2, pt: 1, mt: 'auto' }}>
            <Stack gap={1.5}>
              {/* Server-side: show a 'clear' that triggers navigation */}
              {isServerSideFilter && (
                <Button
                  fullWidth
                  variant="text"
                  color="secondary"
                  onClick={handleClear}
                  disabled={loading ? true : getCurrentString(true) ? false : true}
                  sx={{
                    gap: 0.5,
                    alignItems: 'center',
                  }}
                >
                  <MaterialIcon icon="delete_history" />
                  Limpiar
                </Button>
              )}
              <Button
                fullWidth
                data-testid="filter-reset"
                variant={isServerSideFilter ? 'contained' : 'outlined'}
                onClick={() => {
                  filterAction();
                }}
                disabled={loading}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress size={20} color="inherit" />
                  </Box>
                ) : isServerSideFilter ? (
                  'Buscar'
                ) : (
                  'Limpiar'
                )}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
